import { useState, useRef, useCallback } from 'react'
import { useSetRecoilState } from 'recoil'
import { userEmailSubscriptionState } from 'states/CheckOutStates/atoms'
import { ApolloError, useMutation, useApolloClient } from '@apollo/client';
import { DASHBOARD_CREATE_EMAIL_SUBSCRIPTION, DASHBOARD_EMAIL_SUBSCRIPTIONS_BY_USER, UNSUBSCRIBE_EMAIL, RESUBSCRIBE_EMAIL} from 'graphql/service';

const getSubscriptionErrorMessage = (error: ApolloError) => {
  const networkError = error.networkError;
  const hasGraphQLErrors = error.graphQLErrors.length > 0 || (
    networkError && 'result' in networkError &&
    typeof networkError.result === 'object' && networkError.result?.errors?.length > 0
  );

  return networkError && !hasGraphQLErrors
    ? 'Unable to update your subscription. Please check your internet connection and try again.'
    : error.message;
};

/**
 * A custom hook for Personal details Email Screen to load and edit email subscriptions
 */
export const useEmailSubscription = () => {

  const debug = false;
  
  const userId = localStorage.getItem('userId');

// 
// This is current e-mail subscription we use

//       "subscriptionTypes": [
//           {
//               "id": "5f705113-b914-4cee-b503-72c476ae27ee",
//               "type": "general",
//               "createdAt": "2024-07-29T14:39:00.816Z",
//               "updatedAt": "2024-07-29T14:39:00.817Z"
//           }
//       ]

const generalSubscriptionTypeId = "5f705113-b914-4cee-b503-72c476ae27ee";

  if (debug) {
    console.log ("userId = ", userId);
  }
  
  const setUserEmailSubscriptionState = useSetRecoilState(userEmailSubscriptionState);
  const client = useApolloClient();
  const requestInProgress = useRef(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);

  const [errorState, setErrorState] = useState({
    hasError: false,
    message: '',
  });

  const [suscessState, setSuscessState] = useState({
    isSuscess: false,
    message: '',
  });

  const [dashboardCreateEmailSubscription] = useMutation(DASHBOARD_CREATE_EMAIL_SUBSCRIPTION);
  const [dashboardEmailUnsubscription] = useMutation(UNSUBSCRIBE_EMAIL);
  const [dashboardEmailResubscription] = useMutation(RESUBSCRIBE_EMAIL);

  const fetchEmailSubscription = useCallback(async () => {
    if (!userId) {
      throw new Error('Unable to load email preferences. Please sign in again.');
    }

    const { data } = await client.query<{
      emailSubscriptionsByUser: {
        id: string;
        isCancelled: boolean;
        subscriptionType: { id: string } | null;
      }[];
    }>({
      query: DASHBOARD_EMAIL_SUBSCRIPTIONS_BY_USER,
      variables: { userId },
      fetchPolicy: 'no-cache',
    });

    if (!Array.isArray(data?.emailSubscriptionsByUser)) {
      throw new Error('Unable to load email preferences. Please try again.');
    }

    const activeSubscription = data.emailSubscriptionsByUser.find(
      (subscription) =>
        !subscription.isCancelled &&
        subscription.subscriptionType?.id === generalSubscriptionTypeId
    );
    const subscription = activeSubscription || data.emailSubscriptionsByUser.find(
      (subscription) =>
        subscription.isCancelled &&
        subscription.subscriptionType?.id === generalSubscriptionTypeId
    );

    if (subscription && (typeof subscription.id !== 'string' || !subscription.id.trim())) {
      throw new Error('Unable to load the email subscription ID. Please try again.');
    }

    const subscriptionState = {
      subscriptionID: subscription ? subscription.id : '',
      isUserSubscribed: !!activeSubscription,
      loadDataFromServer: false,
    };
    setUserEmailSubscriptionState(subscriptionState);
    return subscriptionState;
  }, [client, userId, setUserEmailSubscriptionState]);

  const runSubscriptionRequest = useCallback(async (request: () => Promise<void>, isUpdatingSubscription = false) => {
    if (requestInProgress.current) {
      return;
    }

    requestInProgress.current = true;
    setIsSubscriptionLoading(true);
    setErrorState({ hasError: false, message: '' });
    setSuscessState({ isSuscess: false, message: '' });

    try {
      await request();
    } catch (error) {
      setErrorState({
        hasError: true,
        message: isUpdatingSubscription && error instanceof ApolloError
          ? getSubscriptionErrorMessage(error)
          : error instanceof Error ? error.message : 'Unable to update email preferences. Please try again.',
      });
    } finally {
      requestInProgress.current = false;
      setIsSubscriptionLoading(false);
    }
  }, []);

  const handleFetchEmailSubscriptionsByUser = useCallback(() => runSubscriptionRequest(async () => {
    await fetchEmailSubscription();
  }), [fetchEmailSubscription, runSubscriptionRequest]);

  const handleUpdateSubscription = (receiveMarketingEmails: boolean) => runSubscriptionRequest(async () => {
    // Read the current ID before choosing a mutation, including after an email change.
    const subscription = await fetchEmailSubscription();
    if (subscription.isUserSubscribed === receiveMarketingEmails) {
      return;
    }

    if (receiveMarketingEmails) {
      if (subscription.subscriptionID) {
        await dashboardEmailResubscription({
          variables: { id: subscription.subscriptionID },
        });
      } else {
        await dashboardCreateEmailSubscription({
          variables: {
            input: {
              userId: userId,
              subscriptionTypeIds: [generalSubscriptionTypeId]
            }
          },
        });
      }
    } else {
      if (!subscription.subscriptionID) {
        throw new Error('Unable to load the email subscription ID. Please try again.');
      }
      await dashboardEmailUnsubscription({
        variables: { id: subscription.subscriptionID },
      });
    }

    // Reload the current ID and status instead of assuming that a mutation preserves the ID.
    const updatedSubscription = await fetchEmailSubscription();
    if (updatedSubscription.isUserSubscribed !== receiveMarketingEmails) {
      throw new Error('Email preferences were not updated. Please try again.');
    }

    setSuscessState({
      isSuscess: true,
      message: receiveMarketingEmails
        ? "Now you will receive occasional marketing texts from Rook Hotel"
        : "Now you will not receive occasional marketing texts from Rook Hotel",
    });
  }, true);

  return {
    handleFetchEmailSubscriptionsByUser,
    handleUpdateSubscription,
    isSubscriptionLoading,
    errorState,
    setErrorState,
    suscessState,
    setSuscessState
  }
}

